import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ResourcesList from '../components/Ressources/ResourcesList';
import { ResourcesProvider } from '../contexts/ResourcesContext';
import { FileText, GraduationCap, BookOpen, ArrowUp } from 'lucide-react';
import PageSEO from '../components/SEO/PageSEO';

const RessourcesPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Schéma JSON-LD pour les ressources éducatives
  const schema = {
    "@context": "https://schema.org",
    "@type": "DataDownload",
    "name": "Ressources Gratuites SES",
    "description": "Bibliothèque de ressources pédagogiques gratuites pour réviser les Sciences Économiques et Sociales, incluant des fiches de révision, des méthodes pour le bac et des plans détaillés.",
    "contentUrl": "https://cours-ses.fr/ressources",
    "encodingFormat": "PDF",
    "isAccessibleForFree": true,
    "creator": {
      "@type": "Organization",
      "name": "Comprendre les SES",
      "image": "https://cours-ses.fr/Logo SES.png"
    },
    "educationalUse": "Study Resource",
    "learningResourceType": "Handout",
    "educationalLevel": "High School",
    "dateCreated": "2023-09-01",
    "dateModified": "2024-03-15",
    "educationalAlignment": [
      {
        "@type": "AlignmentObject",
        "alignmentType": "educationalSubject",
        "targetName": "Sciences Économiques et Sociales"
      },
      {
        "@type": "AlignmentObject",
        "alignmentType": "educationalLevel",
        "targetName": "Lycée"
      }
    ],
    "about": [
      "Économie",
      "Sociologie",
      "Science politique",
      "Croissance économique",
      "Commerce international",
      "Chômage",
      "Structure sociale",
      "Mobilité sociale",
      "Engagement politique",
      "Environnement"
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageSEO
        title="Ressources Gratuites SES | Fiches, Méthodes et Plans Détaillés"
        description="Téléchargez gratuitement nos fiches de révision SES, guides méthodologiques et plans détaillés pour Seconde, Première et Terminale. Ressources classées par niveau et chapitre."
        path="/ressources"
        image="https://cours-ses.fr/Logo SES.png"
        schema={schema}
      />
      <Navbar />
      <main className="pt-16">
        <section className="py-16 bg-gradient-to-b from-[#9a6ebd]/10 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-[#323c5f] mb-6">
                Ressources Gratuites
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Accédez à notre bibliothèque de ressources pédagogiques pour réviser 
                efficacement les Sciences Économiques et Sociales.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-[#ceace8] hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <FileText className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#323c5f] mb-3">
                  Fiches de révision
                </h3>
                <p className="text-gray-600">
                  Des synthèses claires et structurées pour maîtriser les notions essentielles.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-[#ceace8] hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <GraduationCap className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#323c5f] mb-3">
                  Méthodes Bac
                </h3>
                <p className="text-gray-600">
                  Guides et conseils pour réussir les différentes épreuves du baccalauréat.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-[#ceace8] hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <BookOpen className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#323c5f] mb-3">
                  Plans détaillés
                </h3>
                <p className="text-gray-600">
                  Des plans types pour les sujets tombés au bac, avec analyses et conseils.
                </p>
              </div>
            </div>

            <ResourcesProvider>
              <ResourcesList />
            </ResourcesProvider>
          </div>
        </section>

        {/* Bouton retour en haut */}
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 bg-[#9a6ebd] text-white p-3 rounded-full shadow-lg hover:bg-[#8a5dad] transition-all duration-300 ${
            showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16 pointer-events-none'
          }`}
          aria-label="Retour en haut"
        >
          <ArrowUp size={24} />
        </button>
      </main>
      <Footer />
    </div>
  );
};

export default RessourcesPage;